import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    MenuItem,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Tabs,
    Tab,
} from '@mui/material';
import {
    ArrowBack,
    Download,
    BarChart as BarChartIcon,
    TableChart,
    FilterList,
} from '@mui/icons-material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';

const actionLabels = {
    CLEANED: { label: 'Curățată', color: '#2e7d32' },
    MARKED_DIRTY: { label: 'Marcată murdară', color: '#c62828' },
    IN_PROGRESS: { label: 'În curs', color: '#ef6c00' },
    OUT_OF_ORDER: { label: 'Indisponibilă', color: '#616161' },
};

const PIE_COLORS = {
    CLEAN: '#2e7d32',
    DIRTY: '#c62828',
    IN_PROGRESS: '#ef6c00',
    OUT_OF_ORDER: '#616161',
};

const PIE_LABELS = {
    CLEAN: 'Curată',
    DIRTY: 'Murdară',
    IN_PROGRESS: 'În curs',
    OUT_OF_ORDER: 'Indisponibilă',
};

function Reports() {
    const navigate = useNavigate();
    const [tab, setTab] = useState(1);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [logs, setLogs] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [users, setUsers] = useState([]);
    const [filters, setFilters] = useState({
        roomId: '',
        userId: '',
        action: '',
        startDate: '',
        endDate: '',
    });

    const buildParams = useCallback(() => {
        const params = {};
        if (filters.roomId) params.roomId = filters.roomId;
        if (filters.userId) params.userId = filters.userId;
        if (filters.action) params.action = filters.action;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
        return params;
    }, [filters]);

    const fetchInitialData = async () => {
        try {
            const [statsRes, roomsRes, usersRes, logsRes] = await Promise.all([
                API.get('/reports/stats'),
                API.get('/reports/rooms'),
                API.get('/reports/users'),
                API.get('/reports/cleaning-logs'),
            ]);
            setStats(statsRes.data);
            setRooms(roomsRes.data);
            setUsers(usersRes.data);
            setLogs(logsRes.data);
        } catch (err) {
            console.error('Error fetching report data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, []);

    const handleApplyFilters = async () => {
        setLoading(true);
        try {
            const params = buildParams();
            const [statsRes, logsRes] = await Promise.all([
                API.get('/reports/stats', { params }),
                API.get('/reports/cleaning-logs', { params }),
            ]);
            setStats(statsRes.data);
            setLogs(logsRes.data);
        } catch (err) {
            console.error('Error applying filters:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            const params = buildParams();
            const response = await API.get('/reports/export', {
                params,
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'raport-curatenie.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting:', err);
        }
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleResetFilters = async () => {
        setFilters({ roomId: '', userId: '', action: '', startDate: '', endDate: '' });
        setLoading(true);
        try {
            const [statsRes, logsRes] = await Promise.all([
                API.get('/reports/stats'),
                API.get('/reports/cleaning-logs'),
            ]);
            setStats(statsRes.data);
            setLogs(logsRes.data);
        } catch (err) {
            console.error('Error resetting:', err);
        } finally {
            setLoading(false);
        }
    };

    // Chart data
    const roomStatusData = stats?.roomStatusCounts
        ? Object.entries(stats.roomStatusCounts)
            .filter(([, value]) => value > 0)
            .map(([key, value]) => ({
                name: PIE_LABELS[key] || key,
                value: value,
                color: PIE_COLORS[key] || '#999',
            }))
        : [];

    const actionsPerDayData = stats?.actionsPerDay
        ? Object.entries(stats.actionsPerDay)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, actions]) => ({
                date: date.substring(5),
                Curățată: actions.CLEANED || 0,
                Murdară: actions.MARKED_DIRTY || 0,
                'În curs': actions.IN_PROGRESS || 0,
                Indisponibilă: actions.OUT_OF_ORDER || 0,
            }))
        : [];

    const actionsPerUserData = stats?.actionsPerUser
        ? Object.entries(stats.actionsPerUser)
            .sort(([, a], [, b]) => b - a)
            .map(([name, count]) => ({
                name,
                acțiuni: count,
            }))
        : [];

    if (loading && !stats) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/')}
                        sx={{ mb: 1, color: 'text.secondary' }}
                    >
                        Înapoi la Dashboard
                    </Button>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Rapoarte
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={handleExport}
                    sx={{
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                    }}
                >
                    Export Excel
                </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterList /> Filtre
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Cameră"
                            name="roomId"
                            value={filters.roomId}
                            onChange={handleFilterChange}
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="">Toate</MenuItem>
                            {rooms.sort((a, b) => a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true })).map((room) => (
                                <MenuItem key={room.id} value={room.id}>
                                    {room.roomNumber} - Etaj {room.floor}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Utilizator"
                            name="userId"
                            value={filters.userId}
                            onChange={handleFilterChange}
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="">Toți</MenuItem>
                            {users.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.firstName} {user.lastName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Acțiune"
                            name="action"
                            value={filters.action}
                            onChange={handleFilterChange}
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="">Toate</MenuItem>
                            {Object.entries(actionLabels).map(([key, val]) => (
                                <MenuItem key={key} value={key}>{val.label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="De la"
                            type="date"
                            name="startDate"
                            value={filters.startDate}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Până la"
                            type="date"
                            name="endDate"
                            value={filters.endDate}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button variant="contained" size="small" onClick={handleApplyFilters} sx={{ borderRadius: 2 }}>
                        Aplică filtre
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleResetFilters} sx={{ borderRadius: 2 }}>
                        Resetează
                    </Button>
                </Box>
            </Paper>

            {/* Summary */}
            {stats && (
                <Paper sx={{ p: 2, mb: 3, borderRadius: 3, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Total acțiuni: <Chip label={stats.totalLogs} size="small" color="primary" />
                    </Typography>
                    {stats.totalActions && Object.entries(stats.totalActions).map(([action, count]) => {
                        const config = actionLabels[action];
                        return config ? (
                            <Typography key={action} variant="body2">
                                {config.label}: <Chip label={count} size="small" sx={{ backgroundColor: `${config.color}15`, color: config.color, fontWeight: 600 }} />
                            </Typography>
                        ) : null;
                    })}
                </Paper>
            )}

            {/* Tabs */}
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
                <Tab icon={<BarChartIcon />} label="Grafice" iconPosition="start" />
                <Tab icon={<TableChart />} label="Tabel detaliat" iconPosition="start" />
            </Tabs>

            {/* Charts Tab */}
            {tab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: 450 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            Status camere actual
                        </Typography>
                        {roomStatusData.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                                <Typography color="text.secondary">Nu sunt date</Typography>
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={380}>
                                <PieChart>
                                    <Pie
                                        data={roomStatusData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={140}
                                        innerRadius={60}
                                        paddingAngle={3}
                                        label={({ name, value }) => `${name}: ${value}`}
                                        labelLine={true}
                                    >
                                        {roomStatusData.map((entry, index) => (
                                            <Cell key={index} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>

                    <Paper sx={{ p: 3, borderRadius: 3, height: 450 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            Acțiuni pe zi
                        </Typography>
                        {actionsPerDayData.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                                <Typography color="text.secondary">Nu sunt date pentru perioada selectată</Typography>
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={380}>
                                <BarChart data={actionsPerDayData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="Curățată" fill="#2e7d32" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="Murdară" fill="#c62828" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="În curs" fill="#ef6c00" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="Indisponibilă" fill="#616161" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>

                    <Paper sx={{ p: 3, borderRadius: 3, height: actionsPerUserData.length > 5 ? 500 : 400 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            Acțiuni per utilizator
                        </Typography>
                        {actionsPerUserData.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                <Typography color="text.secondary">Nu sunt date</Typography>
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={actionsPerUserData.length > 5 ? 430 : 330}>
                                <BarChart data={actionsPerUserData} layout="vertical" margin={{ left: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" allowDecimals={false} />
                                    <YAxis dataKey="name" type="category" width={130} />
                                    <Tooltip />
                                    <Bar dataKey="acțiuni" fill="#1a237e" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Box>
            )}

            {/* Table Tab */}
            {tab === 1 && (
                <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                    {logs.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                Nu sunt rezultate. Modifică filtrele și apasă "Aplică filtre".
                            </Typography>
                        </Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Nr.</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Cameră</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Utilizator</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Acțiune</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map((log, index) => {
                                    const config = actionLabels[log.action] || { label: log.action, color: '#616161' };
                                    return (
                                        <TableRow key={log.id} sx={{ '&:hover': { backgroundColor: '#f5f7fa' } }}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{log.roomNumber}</TableCell>
                                            <TableCell>{log.userName}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={config.label}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: `${config.color}15`,
                                                        color: config.color,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{log.notes || '-'}</TableCell>
                                            <TableCell>{new Date(log.createdAt).toLocaleString('ro-RO')}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                    {logs.length > 0 && (
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {logs.length} rezultate
                            </Typography>
                            <Button startIcon={<Download />} onClick={handleExport} size="small">
                                Export Excel
                            </Button>
                        </Box>
                    )}
                </TableContainer>
            )}
        </Box>
    );
}

export default Reports;